import apiClient from './apiService';

// Audit plan timeline — auditor persona (BRs APM-3, APM-5, APM-7 in
// docs/audit-planning-memorandum.html). Backend: backend/routes/adt_plan_routes.py.

const BASE = '/api/v1/adt/plan';

export type PlanPeriodicity = 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'ANNUAL';

/** The four audit-plan timelines an agency may follow (APM-5.1), fixed on the agency definition. */
export const PLAN_TIMELINES: { value: PlanPeriodicity; label: string }[] = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'HALF_YEARLY', label: 'Half-yearly' },
  { value: 'ANNUAL', label: 'Yearly' },
];
export const timelineLabel = (v?: string | null) => PLAN_TIMELINES.find(t => t.value === v)?.label ?? 'Quarterly';
export type PlanPriority = 'High' | 'Medium' | 'Low';

export interface PlanAgency {
  id: number;
  audit_agency_name: string;
  agency_kind: 'Internal' | 'External';
  plan_periodicity: PlanPeriodicity;
  is_head: boolean;
}

export interface PlanPeriod {
  number: number;
  label: string;
  start: string;
  end: string;
  /** Inside the agency's audit period (APM-5.2) — only these can be scoped. */
  in_window: boolean;
}

export type PlanYearType = 'CALENDAR' | 'FINANCIAL';

export interface ReportingPeriod {
  id: number;
  year_start: number;
  period_no: number;
  year_label: string;
  label: string;
  start_date: string;
  end_date: string;
}

export interface PlanStatement {
  id: number;
  agency_id: number;
  fiscal_year: number;
  statement: string;
  detail?: string | null;
  area?: string | null;
  priority: PlanPriority;
  start_date: string;
  end_date: string;
  lead_user_id?: number | null;
  lead_name?: string | null;
  program_template_id?: number | null;
  program_name?: string | null;
  universe_unit_id?: number | null;
  universe_unit_name?: string | null;
  is_active: number;
}

export interface PlanUniverseUnit {
  unit_id: number;
  unit_name: string;
  unit_type: string;
  recommended_program_id?: number | null;
  recommended_program_name?: string | null;
  /** Scoping decisions keyed by period number (as a string). */
  cells: Record<string, PlanScopeCell>;
  scoped_count: number;
  rated_count: number;
}

export type PlanRiskRating = 'High' | 'Medium' | 'Low';

/** One plan cell (unit × period): the risk rating and the scoping decision are independent (APM-8, APM-9). */
export interface PlanScopeCell {
  is_scoped: boolean;
  reason?: string | null;
  decided_by?: string | null;
  decided_on?: string | null;
  risk_rating?: PlanRiskRating | null;
  risk_note?: string | null;
  rated_by?: string | null;
  rated_on?: string | null;
}

export interface PlanUniverse {
  id: number;
  universe_code: string;
  universe_name: string;
  category: string;
  plan_due_date?: string | null;
  units: PlanUniverseUnit[];
}

export interface PlanAudit {
  id: number;
  status?: string | null;
  workflow_state?: string | null;
  start_date: string;
  due_date?: string | null;
  program_name?: string | null;
  lead_name?: string | null;
}

export interface PlanTimeline {
  agency: PlanAgency;
  year_type: PlanYearType;
  plan_window: { start_label: string; end_label?: string | null };
  universes: PlanUniverse[];
  fiscal_year: number;
  fiscal_year_label: string;
  fiscal_year_start: string;
  fiscal_year_end: string;
  fiscal_years: { value: number; label: string }[];
  periodicity: PlanPeriodicity;
  periods: PlanPeriod[];
  statements: PlanStatement[];
  audits: PlanAudit[];
  members: { id: number; name: string }[];
  programs: { id: number; name: string; ownership_scope: string }[];
  can_edit: boolean;
}

export interface PlanStatementInput {
  agency_id: number;
  statement: string;
  detail?: string | null;
  area?: string | null;
  priority: PlanPriority;
  start_date: string;
  end_date: string;
  lead_user_id?: number | null;
  program_template_id?: number | null;
  universe_unit_id?: number | null;
}

export const auditPlanService = {
  /** APM-5.2 — reporting periods to pick an agency's start / end period from. */
  async getPeriods(yearType: PlanYearType, periodicity: PlanPeriodicity): Promise<ReportingPeriod[]> {
    return (await apiClient.get(`${BASE}/periods`, { params: { year_type: yearType, periodicity } })).data;
  },
  async getAgencies(): Promise<PlanAgency[]> {
    const resp = await apiClient.get(`${BASE}/agencies`);
    return resp.data;
  },
  async getTimeline(agencyId: number, fiscalYear?: number): Promise<PlanTimeline> {
    const resp = await apiClient.get(`${BASE}/timeline`, {
      params: { agency_id: agencyId, ...(fiscalYear ? { fiscal_year: fiscalYear } : {}) },
    });
    return resp.data;
  },
  async createStatement(body: PlanStatementInput): Promise<{ id: number }> {
    const resp = await apiClient.post(`${BASE}/statements`, body);
    return resp.data;
  },
  async updateStatement(id: number, body: PlanStatementInput): Promise<void> {
    await apiClient.put(`${BASE}/statements/${id}`, body);
  },
  /** APM-9: scope a unit in (reason required) or out for one period. */
  async setScoping(body: {
    agency_id: number; fiscal_year: number; universe_unit_id: number;
    period_no: number; is_scoped: boolean; reason?: string | null;
  }): Promise<void> {
    await apiClient.put(`${BASE}/scoping`, body);
  },
  /** APM-8: rate a unit's risk for one period (null clears it). Never scopes it in. */
  async setRating(body: {
    agency_id: number; fiscal_year: number; universe_unit_id: number;
    period_no: number; risk_rating: PlanRiskRating | null; risk_note?: string | null;
  }): Promise<void> {
    await apiClient.put(`${BASE}/rating`, body);
  },
  async setStatementStatus(id: number, is_active: 0 | 1): Promise<void> {
    await apiClient.patch(`${BASE}/statements/${id}/status`, { is_active });
  },
};

export default auditPlanService;
